import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

// SVG Icons
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

function ContactChannel({ icon, label, value, desc, index }) {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="contact-channel" style={{ transitionDelay: `${index * 100}ms` }}>
      <div className="contact-channel__icon">{icon}</div>
      <div className="contact-channel__label">{label}</div>
      <div className="contact-channel__value">{value}</div>
      <div className="contact-channel__desc">{desc}</div>
    </div>
  );
}

function ContactAddressCard({ factory, detail, imageUrl, tag, index }) {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="contact-address-card" style={{ transitionDelay: `${index * 120}ms` }}>
      <div className="contact-address-card__header">
        <img
          className="contact-address-card__header-img"
          src={imageUrl}
          alt={factory}
          loading="lazy"
        />
        <div className="contact-address-card__header-overlay" />
        <div className="contact-address-card__tag">
          <span>{tag}</span>
        </div>
      </div>
      <div className="contact-address-card__body">
        <div className="contact-address-card__factory">{factory}</div>
        <p className="contact-address-card__detail">{detail}</p>
      </div>
    </div>
  );
}

export default function Contact() {
  const { t } = useTranslation();

  return (
    <>
    <SEO
        pageKey="contact"
        title={PAGE_SEO.contact.title}
        description={PAGE_SEO.contact.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.contact.slug}
    />
    <div className="contact-page">
      {/* Hero */}
      <div className="contact-hero">
        <img
          className="contact-hero__img"
          src="/files/images/companybg.jpg"
          alt="Xiaomai"
        />
        <div className="contact-hero__overlay" />
        <div className="contact-hero__content">
          <div className="contact-hero__eyebrow">
            {t('contact_hero_eyebrow')}
          </div>
          <h1 className="contact-hero__title">
            {t('contact_hero_title')}
          </h1>
          <p className="contact-hero__sub">
            {t('contact_hero_sub')}
          </p>
          <div className="contact-hero__rule" />
        </div>
      </div>

      {/* Contact Channels */}
      <section className="contact-channels">
        <div className="contact-channels__grid">
          <ContactChannel
            icon={<PhoneIcon />}
            label={t('contact_channel_hotline_label')}
            value="400-050-1183"
            desc={t('contact_channel_hotline_desc')}
            index={0}
          />
          <ContactChannel
            icon={<EmailIcon />}
            label={t('contact_channel_email_label')}
            value="gnaturepowdef@gmail.com"
            desc={t('contact_channel_email_desc')}
            index={1}
          />
          <ContactChannel
            icon={<GlobeIcon />}
            label={t('contact_channel_website_label')}
            value="xiaomai.tech"
            desc={t('contact_channel_website_desc')}
            index={2}
          />
        </div>
      </section>

      {/* Factory Addresses */}
      <section className="contact-addresses">
        <div className="contact-addresses__header">
          <div className="contact-addresses__eyebrow">
            {t('contact_addresses_eyebrow')}
          </div>
          <h2 className="contact-addresses__title">
            {t('contact_addresses_title')}
          </h2>
          <div className="contact-addresses__rule" />
        </div>

        <div className="contact-addresses__grid">
          <ContactAddressCard
            factory={t('contact_factory_jiangxi')}
            detail={t('contact_factory_jiangxi_detail')}
            imageUrl="/files/images/companybg.jpg"
            tag={t('contact_factory_tag_hq')}
            index={0}
          />
          <ContactAddressCard
            factory={t('contact_factory_dongguan')}
            detail={t('contact_factory_dongguan_detail')}
            imageUrl="/files/images/companybg.jpg"
            tag={t('contact_factory_tag_origin')}
            index={1}
          />
        </div>
      </section>

      {/* WhatsApp CTA */}
      <div className="contact-cta">
        <div className="contact-cta__inner">
          <p className="contact-cta__text">
            {t('contact_cta_text')}
          </p>
          <p className="contact-cta__sub">
            {t('contact_cta_sub')}
          </p>
          <a
            className="contact-cta__btn"
            href="https://wa.me/8618929131303?text=I%20am%20very%20interested%20in%20the%20fabric%20laying%20machine%20you%20are%20selling!"
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon />
            {t('contact_cta_btn')}
          </a>
        </div>
      </div>
    </div>
    </>
  );
}
